import { createContext, useContext, useMemo, type ReactElement, type ReactNode } from "react";
import { BrowserRouter } from "react-router";
import { ClmQueryProvider } from "@/app/providers/query-provider";
import { createLocalRuntimeConfig, setRuntimeConfigForTests } from "@/app/config/runtime-config";
import { AuthProvider } from "@/core/auth/auth-provider";
import { WriteAuthorityProvider } from "@/core/write-authority/write-authority-provider";
import type { RuntimeConfig } from "@/types/clm-web";

export type RuntimeConfigMode = "loaded" | "test";

export interface AppProvidersProps {
  children: ReactNode;
  initialRuntimeConfig?: RuntimeConfig;
  runtimeConfigMode?: RuntimeConfigMode;
}

const RuntimeConfigContext = createContext<RuntimeConfig | undefined>(undefined);

export function AppProviders({
  children,
  initialRuntimeConfig,
  runtimeConfigMode = "loaded",
}: AppProvidersProps): ReactElement {
  const runtimeConfig = useMemo(() => {
    const config = initialRuntimeConfig ?? createLocalRuntimeConfig();

    if (runtimeConfigMode === "test") {
      setRuntimeConfigForTests(config);
    }

    return config;
  }, [initialRuntimeConfig, runtimeConfigMode]);

  return (
    <RuntimeConfigContext.Provider value={runtimeConfig}>
      <ClmQueryProvider>
        <AuthProvider>
          <WriteAuthorityProvider>
            <BrowserRouter>{children}</BrowserRouter>
          </WriteAuthorityProvider>
        </AuthProvider>
      </ClmQueryProvider>
    </RuntimeConfigContext.Provider>
  );
}

export function useAppRuntimeConfig(): RuntimeConfig {
  const config = useContext(RuntimeConfigContext);

  if (!config) {
    throw new Error("App runtime config is not available. Render within AppProviders.");
  }

  return config;
}
